import './WinningHistory.css';
import { useState, useEffect } from 'react';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { WalletContext } from './WalletContext';

function WinningHistory({ onBack }) {
  const navigate = useNavigate();
  const { walletBalance } = useContext(WalletContext);
  const [winnings, setWinnings] = useState([]);
  const [filteredWinnings, setFilteredWinnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const [normalGames, setNormalGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [filters, setFilters] = useState({
    gameName: '',
    gameType: '',
    session: ''
  });

  // Fetch winning history on mount
  useEffect(() => {
    fetchWinningHistory();
    fetchNormalGames();
  }, []);

  // Fetch normal games dropdown
  const fetchNormalGames = async () => {
    try {
      setGamesLoading(true);
      const response = await fetch('https://backend.mainbazar777.site/api/fetch-normal-game');
      if (!response.ok) return;
      const data = await response.json();
      // console.log('Normal games fetched:', data);
      if (data.success && data.normal_games) {
        setNormalGames(data.normal_games);
      }
    } catch (err) {
      console.error('Error fetching normal games:', err);
    } finally {
      setGamesLoading(false);
    }
  };

  // Apply filters when winnings or filters change
  useEffect(() => {
    applyFilters();
    setCurrentPage(1);
  }, [winnings, filters]);

  const fetchWinningHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const userData = localStorage.getItem('user');
      const user = userData ? JSON.parse(userData) : null;
      const userId = user?.id;

      if (!userId) {
        setError('User ID not found. Please login again.');
        return;
      }

      const response = await fetch(
        `https://backend.mainbazar777.site/api/normal-win/${userId}`
      );
      const result = await response.json();

      if (result.success && result.normal_wins) {
        setWinnings(result.normal_wins);
      } else {
        setError('Failed to fetch winning history');
      }
    } catch (err) {
      setError('Error fetching winning history: ' + err.message);
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = winnings;

    if (filters.gameName) {
      filtered = filtered.filter(item =>
        item.normal_game.id.toString() === filters.gameName
      );
    }

    if (filters.gameType) {
      filtered = filtered.filter(item =>
        item.normal_game_type.toLowerCase().includes(filters.gameType.toLowerCase())
      );
    }

    if (filters.session) {
      filtered = filtered.filter(item =>
        item.wins.bid_session.toLowerCase() === filters.session.toLowerCase()
      );
    }

    setFilteredWinnings(filtered);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      gameName: '',
      gameType: '',
      session: ''
    });
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredWinnings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedWinnings = filteredWinnings.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = new Set(
      [1, 2, currentPage - 1, currentPage, currentPage + 1, totalPages]
        .filter(p => p >= 1 && p <= totalPages)
    );
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
      result.push(sorted[i]);
    }
    return result;
  };

  return (
    <div className="winning-history-page">
      {/* Header */}
      <header className="wh-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <h1>Winning History</h1>
        <button className="wallet-btn" onClick={() => navigate("/funds")}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
            <path d="M2 10H22" stroke="white" strokeWidth="2"/>
            <circle cx="18" cy="15" r="1.5" fill="white"/>
          </svg>
          <span className="wallet-balance">₹ {walletBalance}</span>
        </button>
      </header>

      {/* Content */}
      <div className="wh-content">
        {loading && <div className="loading">Loading winning history...</div>}
        {error && <div className="error">{error}</div>}

        {!loading && !error && (
          <>
            {/* Filters Section */}
            <div className="filters-section">
              <h4>Filters</h4>
              <div className="filters-grid">
                <div className="filter-group">
                  <label>Game Name</label>
                  <select
                    value={filters.gameName}
                    onChange={(e) => handleFilterChange('gameName', e.target.value)}
                    disabled={gamesLoading}
                  >
                    <option value="">All Games</option>
                    {normalGames.map((game) => (
                      <option key={game.id} value={game.id}>
                        {game.game_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label>Game Type</label>
                  <select
                    value={filters.gameType}
                    onChange={(e) => handleFilterChange('gameType', e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="Single Digit">Single Digit</option>
                    <option value="Jodi">Jodi</option>
                    <option value="Single Panna">Single Panna</option>
                    <option value="Double Panna">Double Panna</option>
                    <option value="Triple Panna">Triple Panna</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Session</label>
                  <select
                    value={filters.session}
                    onChange={(e) => handleFilterChange('session', e.target.value)}
                  >
                    <option value="">All Sessions</option>
                    <option value="open">Open</option>
                    <option value="close">Close</option>
                  </select>
                </div>
              </div>
              <button className="clear-filters-btn" onClick={clearFilters}>Clear Filters</button>
            </div>

            {/* Winning History Cards */}
            <div className="winning-history-list">
              <h4>Winning History ({filteredWinnings.length})</h4>
              {filteredWinnings.length === 0 ? (
                <div className="no-data">
                  {winnings.length === 0
                    ? 'No winning history found'
                    : 'No wins match the selected filters'}
                </div>
              ) : (
                <>
                  <div className="cards-container">
                    {paginatedWinnings.map((item, index) => (
                      <div key={index} className="winning-card">
                        <div className="card-header">
                          <div className="game-info">
                            <h5 className="game-name">{item.normal_game.game_name}</h5>
                            <p className="game-time">
                              {item.normal_game.open} - {item.normal_game.close}
                            </p>
                          </div>
                          <div className="winning-badge">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                              <path d="M12 2L15.09 10.26H24L17.55 15.74L20.64 24L12 19.52L3.36 24L6.45 15.74L0 10.26H8.91L12 2Z" fill="#4CAF50"/>
                            </svg>
                            Won
                          </div>
                        </div>

                        <div className="card-body">
                          <div className="info-row">
                            <span className="label">Game Type:</span>
                            <span className="value">{item.normal_game_type}</span>
                          </div>
                          <div className="info-row">
                            <span className="label">Bid Number:</span>
                            <span className="value bid-number">{item.wins.bid_number}</span>
                          </div>
                          <div className="info-row">
                            <span className="label">Session:</span>
                            <span className="value session-badge">{item.wins.bid_session}</span>
                          </div>
                          <div className="info-row">
                            <span className="label">Bet Amount:</span>
                            <span className="value">₹ {parseFloat(item.wins.bet_amount).toFixed(2)}</span>
                          </div>
                        </div>

                        <div className="card-footer">
                          <div className="winning-info">
                            <span className="label">Winning Amount:</span>
                            <span className="winning-amount">
                              ₹ {parseFloat(item.wins.winning_amount).toFixed(2)}
                            </span>
                          </div>
                          <div className="profit">
                            <span className="label">Profit:</span>
                            <span className="profit-amount">
                              ₹ {(parseFloat(item.wins.winning_amount) - parseFloat(item.wins.bet_amount)).toFixed(2)}
                            </span>
                          </div>
                        </div>

                        <div className="card-date">
                          {new Date(item.wins.created_at).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        ← Previous
                      </button>

                      <div className="page-numbers">
                        {getPageNumbers().map((page, i) =>
                          page === '...' ? (
                            <span key={`e-${i}`} className="page-ellipsis">...</span>
                          ) : (
                            <button
                              key={page}
                              className={`page-number ${currentPage === page ? 'active' : ''}`}
                              onClick={() => goToPage(page)}
                            >
                              {page}
                            </button>
                          )
                        )}
                      </div>

                      <button
                        className="pagination-btn"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next →
                      </button>
                    </div>
                  )}

                  <div className="pagination-info">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredWinnings.length)} of {filteredWinnings.length} wins
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default WinningHistory;
